import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useClassifierServiceDeleteInvestigationTypeClassifierEndpoint,
  useClassifierServiceGetInvestigationClassifierListEndpoint,
  useClassifierServiceGetInvestigationClassifierListEndpointKey
} from 'src/api/queries'
import { ApiError, ClassifierCategory, ClassifierType } from 'src/api/requests'
import {
  ClassifierInfiniteSelect,
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  TableActionButtons
} from 'src/components'
import { useClassifierId, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { InvestigationTypeModal } from '../components'

export const InvestigationTypesPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()
  const [categories, setCategories] = useState<string[]>([])

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    categoryIds: categories,
    isActive: true
  }

  const { data, isLoading } =
    useClassifierServiceGetInvestigationClassifierListEndpoint(queryParams)

  const { mutate } =
    useClassifierServiceDeleteInvestigationTypeClassifierEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(
          t('laboratory_classifiers.delete_success_investigation_type')
        )
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetInvestigationClassifierListEndpointKey
          ]
        })
      }
    })

  const investigationCategoryId = useClassifierId(
    ClassifierType.INVESTIGATION_CATEGORY,
    ClassifierCategory.INVESTIGATIONS
  )

  const onToggle = () => setIsOpen((prev) => !prev)

  const onBackClick = () => navigate(-1)

  const onCloseModal = () => {
    setCurrentId(undefined)
    onToggle()
  }

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        onToggle()
      }
    },
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('laboratory_classifiers.delete_investigation_type')}
          onConfirm={() =>
            mutate({
              id: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnsType = [
    {
      key: 'name',
      title: t('laboratory_classifiers.investigation_title'),
      render: ({ title }) => title?.[currentLang?.value]
    },
    {
      key: 'short name',
      title: t('laboratory_classifiers.investigation_short_title'),
      render: ({ displayName }) => displayName?.[currentLang?.value]
    },
    {
      key: 'comments',
      title: t('common.comments'),
      render: (_, { comments }) => comments || '-'
    },
    {
      key: 'order',
      title: t('common.order'),
      render: (_, { order }) => order ?? '-'
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getItems(id)} />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('laboratory_classifiers.investigation_type')}
      items={[
        { title: t('menu.laboratory_classifiers') },
        { title: t('laboratory_classifiers.investigation_type') }
      ]}
    >
      <Flex vertical>
        <Flex justify="space-between">
          <Button icon={<LeftOutlined />} onClick={onBackClick}>
            {t('common.back')}
          </Button>

          <Flex gap={16}>
            <ClassifierInfiniteSelect
              id={investigationCategoryId}
              mode="multiple"
              placeholder={t('classifiers.category')}
              maxTagCount={1}
              onChange={(categories: string[]) => setCategories(categories)}
            />

            <Button type="primary" onClick={onToggle}>
              {t('laboratory_classifiers.add_investigation_type')}
            </Button>
          </Flex>
        </Flex>

        <Divider />

        <Table
          rowKey="id"
          columns={columns}
          loading={isLoading}
          dataSource={data?.data}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {isOpen && (
        <InvestigationTypeModal id={currentId} onCancel={onCloseModal} />
      )}
    </PageBreadcrumbs>
  )
}
